import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ControlContainer, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  DaterangepickerDirective,
  DefaultLocaleConfig,
  LOCALE_CONFIG,
  LocaleService,
  NgxDaterangepickerMd,
} from 'ngx-daterangepicker-material';
import dayjs, { type Dayjs } from 'dayjs/esm';
import { AncButtonDirective } from '../anc-button/anc-button.directive';
import type {
  AncDatePickerBound,
  AncDatePickerFilterDisplay,
  AncDatePickerLocale,
  AncDatePickerPopupAlign,
  AncDatePickerSize,
  AncDatePickerValue,
  AncDatePickerVariant,
} from './anc-date-picker.models';
import { AncFieldValidateDirective } from '../../form-validation/anc-field-validate.directive';
import { AncKeepInViewDirective } from '../../directives/anc-keep-in-view.directive';
import { ancShowFieldError } from '../../form-validation/anc-show-field-error.util';

const BODY_THEME_CLASS = 'anc-date-picker-theme';
/**
 * `anc-date-picker-theme` is one body class shared by every picker on the page,
 * so ownership is counted instead of tracked per instance. `onFocusIn` claims
 * the theme and nothing releases it on focus-out, so two pickers in the same
 * form both hold it; letting the first one to finish (`onDatesUpdated`) strip
 * the class stranded the other with its own flag still set, unable to re-add it.
 * Both the DS popup skin and its placement live under this class, so that
 * picker then opened as the unstyled, mis-anchored ngx calendar.
 */
let bodyThemeRefCount = 0;
const POPUP_GAP_PX = 10;
/** Reserve space for sticky side-panel footers when measuring viewport fit. */
const STICKY_FOOTER_CLEARANCE_PX = 80;
/** Above side panels (1010) and modals (1050) so the calendar is not clipped. */
const POPUP_Z_INDEX = 1100;

/** DS month/year grid overlay view modes. */
type AncDatePickerView = 'date' | 'month' | 'year';
type AncDatePickerSide = 'left' | 'right';

const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
/** Years shown per page in the year grid (3 cols × 4 rows). */
const YEAR_GRID_SIZE = 12;
/** Offset of the window start from the anchor year so the anchor sits mid-grid. */
const YEAR_GRID_OFFSET = 5;

/** ngx `writeValue` payload — Moment/Dayjs at runtime. */
type NgxPickerWriteValue = Parameters<DaterangepickerDirective['writeValue']>[0];

function isNgxPickerWriteValue(value: unknown): value is NgxPickerWriteValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    'startDate' in value &&
    'endDate' in value
  );
}

/**
 * DS-styled wrapper around ngxDaterangepickerMd.
 *
 * **Option A — projection (default):** developer supplies `<input ngxDaterangepickerMd>` + optional `[ancError]`.
 *
 * **Option B — declarative:** set `controlName`; lib renders the date input internally with validation.
 */
@Component({
  selector: 'lib-anc-date-picker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    NgxDaterangepickerMd,
    AncButtonDirective,
    AncFieldValidateDirective,
  ],
  templateUrl: './anc-date-picker.component.html',
  styleUrls: ['./anc-date-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: LOCALE_CONFIG, useValue: {} },
    LocaleService,
  ],
  hostDirectives: [AncKeepInViewDirective],
  host: {
    class: 'anc-date-picker',
    '[class.anc-date-picker--field]': 'variant === "field"',
    '[class.anc-date-picker--filter]': 'variant === "filter"',
    '[class.anc-date-picker--single]': 'singleDatePicker',
    '[class.anc-date-picker--embedded]': 'embedded()',
    '[class.anc-date-picker--popup-left]': 'resolvedPopupAlign === "left"',
    '[class.anc-date-picker--popup-right]': 'resolvedPopupAlign === "right"',
    '[class.anc-date-picker--size-default]': 'size === "default"',
    '[class.anc-date-picker--size-compact]': 'size === "compact"',
    '[class.anc-date-picker--size-small]': 'size === "small"',
    '(focusin)': 'onFocusIn()',
    '(focusout)': 'onFocusOut($event)',
  },
})
export class AncDatePickerComponent
  implements AfterContentInit, AfterViewInit, OnChanges, OnDestroy
{
  pickerDirective?: DaterangepickerDirective;

  /**
   * Setter (not a plain `@ViewChild` field) because the internal input lives behind
   * `@if (embeddedControl())` — the control often arrives after `ngAfterViewInit`,
   * so patching there would miss it and ngx's own `setPosition` (absolute, one-shot,
   * LTR-only) would run instead.
   */
  @ViewChild(DaterangepickerDirective, { static: false })
  set pickerDirectiveQuery(directive: DaterangepickerDirective | undefined) {
    this.pickerDirective = directive;
    if (directive) {
      this.patchPickerPosition(directive);
    }
  }

  @ContentChild(DaterangepickerDirective, { static: false })
  projectedPicker?: DaterangepickerDirective;

  /**
   * The DS month/year overlay is `position: fixed` at viewport coordinates, but a
   * `will-change`/`transform`/`filter` ancestor (e.g. `ng-scrollbar`'s
   * `will-change: scroll-position` on its viewport, used by every scrollable panel
   * this picker opens inside) makes CSS resolve `fixed` against that ancestor
   * instead of the viewport, silently offsetting the overlay away from the popup
   * it's meant to sit over (OEM-7025). Re-parenting it to `document.body` escapes
   * any such ancestor so the computed coordinates are accurate again.
   */
  @ViewChild('dpOverlay', { static: false })
  set dpOverlayEl(el: ElementRef<HTMLElement> | undefined) {
    if (el) {
      this.renderer.appendChild(document.body, el.nativeElement);
    }
  }

  @Input() variant: AncDatePickerVariant = 'field';
  /** Height/padding variant — `default` 42px, `compact` 40px, `small` 32px. */
  @Input() size: AncDatePickerSize = 'default';
  @Input() filterDisplay: AncDatePickerFilterDisplay = 'button';
  @Input() label = '';
  @Input() required = false;
  @Input() placeholder = '';
  @Input() filterLabel = 'Date Range';
  @Input() active = false;
  @Input() singleDatePicker = false;
  @Input() disabled = false;
  /**
   * Popup horizontal alignment relative to the trigger field.
   * `'left'` aligns to the field's inline-start; `'right'` to inline-end.
   * Mirrors automatically when `html[dir='rtl']` — no per-page binding needed.
   */
  @Input() popupAlign: AncDatePickerPopupAlign = 'right';
  @Input() drops = 'down';
  @Input() locale: AncDatePickerLocale | null = null;
  @Input() minDate?: AncDatePickerBound;
  @Input() maxDate?: AncDatePickerBound;
  @Input() isInvalidDate?: (m: Dayjs) => boolean;
  @Input() showDropdowns = true;
  @Input() showCancel = true;
  @Input() linkedCalendars?: boolean;
  /** Parent-owned date range (filter variant). Not `ngModel` — avoids NgModel directive conflict. */
  @Input() dateRangeValue: AncDatePickerValue | null = null;

  @Output() datesUpdated = new EventEmitter<AncDatePickerValue>();
  @Output() dateRangeValueChange = new EventEmitter<AncDatePickerValue | null>();

  /**
   * ngx ngModel payload. Always Dayjs (never ISO strings): ngx parses strings with
   * `locale.format` (`MM/DD/YYYY`), so `YYYY-MM-DD` from localStorage → Invalid Date / NaN.
   */
  protected pickerModel: AncDatePickerValue | null = null;

  /** Reactive-forms control name — when set, lib renders the date input internally. */
  controlName = input<string>('');
  /** Forwards to embedded input `ancFieldValidate` (invalid border when submitted). */
  validateSubmitted = input(false, { alias: 'ancFieldValidateSubmitted' });
  /** Option B — required validation message (rendered internally; no projected `[ancError]`). */
  requiredErrorMessage = input<string>('');

  protected readonly focused = signal(false);
  protected readonly hasValue = signal(false);
  protected readonly floating = computed(
    () => this.focused() || this.hasValue()
  );

  /** DS month/year picker overlay state (replaces ngx native month/year selects). */
  protected readonly pickerView = signal<AncDatePickerView>('date');
  protected readonly activeSide = signal<AncDatePickerSide>('left');
  /** Anchor year driving the month grid and the year-grid window. */
  protected readonly viewYear = signal<number>(new Date().getFullYear());
  /** Inline style placing the overlay flush over the open ngx popup. */
  protected readonly overlayStyle = signal<Record<string, string>>({});
  /** Bump to recompute selection/disabled state after ngx re-renders. */
  protected readonly pickerRev = signal(0);

  protected readonly months = MONTH_LABELS;

  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);
  private readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  /** Parent `[formGroup]` / `formGroupName` — not visible to `formControlName` inside this template. */
  private readonly parentForm = inject(ControlContainer, { optional: true, skipSelf: true });
  private observer?: MutationObserver;
  private themeActive = false;
  private readonly defaultIsInvalidDate = (_m: Dayjs): boolean => false;
  private readonly patchedPickers = new WeakSet<DaterangepickerDirective>();
  /** Prevents repeated writeValue during edit-mode prefilled sync. */
  private embeddedValueSynced = false;
  private repositionListener?: () => void;
  private decoratedPopups: HTMLElement[] = [];
  private internalPickerInteraction = false;
  private popupResizeObserver?: ResizeObserver;
  private observedPopup?: HTMLElement;
  private pendingPositionFrame?: number;
  /** True while the popup is deliberately hidden until its settled position is known — see {@link revealSettledPopup}. */
  private popupPendingReveal = false;

  protected hasProjectedInput = false;

  get effectiveLinkedCalendars(): boolean {
    if (this.linkedCalendars !== undefined) {
      return this.linkedCalendars;
    }
    return this.variant === 'filter' && !this.singleDatePicker;
  }

  get effectiveLocale(): AncDatePickerLocale {
    return {
      ...DefaultLocaleConfig,
      format: 'MM/DD/YYYY',
      applyLabel: this.translate.instant('Apply'),
      cancelLabel: this.translate.instant('Cancel'),
      ...(this.locale ?? {}),
    };
  }

  /** ngx-daterangepicker calls this unconditionally in buildCells — must be a function. */
  get effectiveIsInvalidDate(): (m: Dayjs) => boolean {
    return this.isInvalidDate ?? this.defaultIsInvalidDate;
  }

  get filterButtonVariant(): 'primary' | 'tertiary' {
    return this.active ? 'primary' : 'tertiary';
  }

  /** ngx-daterangepicker-material requires Dayjs; consumers may pass Moment or strings. */
  get effectiveMinDate(): Dayjs | undefined {
    return this.normalizeBoundDate(this.minDate);
  }

  get effectiveMaxDate(): Dayjs | undefined {
    return this.normalizeBoundDate(this.maxDate);
  }

  /** Physical left/right after mirroring logical `popupAlign` for RTL. */
  get resolvedPopupAlign(): AncDatePickerPopupAlign {
    const isRtl =
      typeof document !== 'undefined' &&
      document.documentElement.getAttribute('dir') === 'rtl';
    if (!isRtl) {
      return this.popupAlign;
    }
    return this.popupAlign === 'left' ? 'right' : 'left';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dateRangeValue']) {
      this.pickerModel = this.normalizeRangeValue(this.dateRangeValue);
    }
    if (changes['popupAlign']) {
      this.repositionOpenPopup();
    }
  }

  ngAfterContentInit(): void {
    this.hasProjectedInput = !!this.projectedPicker;
    if (this.projectedPicker) {
      this.patchPickerPosition(this.projectedPicker);
    }
  }

  ngAfterViewInit(): void {
    this.patchActivePickerPosition();
    this.updateHasValue();
    Promise.resolve().then(() => this.updateHasValue());

    if (this.controlName()) {
      const control = this.embeddedControl();
      this.watchControl(control);
      setTimeout(() => this.syncEmbeddedValueOnce(control));
    }

    this.observer = new MutationObserver(() => {
      this.updateHasValue();
      this.cdr.markForCheck();
    });
    this.observer.observe(this.host.nativeElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'value'],
    });
  }

  ngOnDestroy(): void {
    this.dismissAndCleanupPicker();
    this.observer?.disconnect();
    this.detachRepositionListeners();
    this.detachHeaderTriggers();
    this.deactivateBodyTheme();
  }

  /** Opens the picker programmatically (filter button / icon trigger). */
  open(event?: Event): void {
    event?.stopPropagation();
    this.resetPickerView();
    // Held invisible until `computePopupPosition`'s settle-check confirms the
    // popup's width has stopped changing (see `linkedCalendars` note there) —
    // otherwise the visible, still-mid-reflow popup jumps across a couple of
    // frames as it's repositioned, reading as an unwanted "slide" animation.
    this.popupPendingReveal = true;
    this.activateBodyTheme();
    this.patchActivePickerPosition();
    const directive = this.pickerDirective ?? this.projectedPicker;
    // ngx show() no-ops when isShown — reset a stuck open from a prior outside-click race.
    if (directive?.picker?.isShown) {
      directive.hide();
    }
    const popup = directive?.picker?.pickerContainer?.nativeElement as
      | HTMLElement
      | undefined;
    popup?.style.removeProperty('display');
    // ngbDropdown teardown can fire document:click after a deferred open — brief grace window.
    this.internalPickerInteraction = true;
    setTimeout(() => {
      this.internalPickerInteraction = false;
    }, 300);
    directive?.open(event);
    if (directive) {
      this.schedulePopupReposition(directive);
    }
  }

  onDatesUpdated(event: AncDatePickerValue): void {
    // ngx emits datesUpdated from exactly two places: clickApply() — always
    // with BOTH bounds set, single-date mode included — and clear(), always
    // with both null. And its directive calls clear() for ANY falsy value
    // written through ngModel, which includes the initial `pickerModel = null`
    // on every mount. Re-emitting that echo told consumers the user had
    // cleared the filter, wiping a range restored from outside (and the null
    // it wrote back into pickerModel fed ngx another clear).
    if (event?.startDate == null || event?.endDate == null) {
      return;
    }
    this.resetPickerView();
    this.pickerModel = this.normalizeRangeValue(event);
    this.datesUpdated.emit(event);
    this.dateRangeValueChange.emit(event);
    this.updateHasValue();
    this.detachRepositionListeners();
    this.disconnectPopupResizeObserver();
    this.deactivateBodyTheme();
    this.cdr.markForCheck();
  }

  onFilterButtonClick(event: Event): void {
    this.open(event);
  }

  protected calendarTriggerAriaLabel(): string {
    const label = this.label?.trim();
    return label
      ? `${this.translate.instant(label)} calendar`
      : this.translate.instant('Open calendar');
  }

  onIconClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLButtonElement | null)?.blur();
    this.open(event);
  }

  protected onFocusIn(): void {
    this.focused.set(true);
    this.activateBodyTheme();
    this.cdr.markForCheck();
  }

  protected onFocusOut(event: FocusEvent): void {
    if (this.host.nativeElement.contains(event.relatedTarget as Node)) {
      return;
    }
    this.focused.set(false);
    this.updateHasValue();
    // Do NOT detach reposition listeners here: the popup is portaled to <body>, so
    // clicking inside it blurs the input while the calendar is still open — dropping
    // the scroll listener is what let the popup drift away from its field.
    // They self-detach on the next scroll once the picker is hidden, and on destroy.
    this.cdr.markForCheck();
  }

  protected updateHasValue(): void {
    const root = this.host.nativeElement as HTMLElement;
    const input = root.querySelector(
      'input.anc-date-picker__input, input[ngxDaterangepickerMd]'
    ) as HTMLInputElement | null;
    const filled =
      !!input?.value?.trim() ||
      !!this.pickerModel?.startDate ||
      !!this.pickerModel?.endDate ||
      !!this.dateRangeValue?.startDate ||
      !!this.dateRangeValue?.endDate;
    this.hasValue.set(filled);
  }

  protected embedded(): boolean {
    return (
      this.variant === 'field' &&
      !this.label &&
      !!this.host.nativeElement.closest('lib-anc-form-field')
    );
  }

  /**
   * Resolves the reactive control from the parent form for embedded mode.
   * `formControlName` cannot be used on the input inside this component template.
   */
  protected embeddedControl(): FormControl | null {
    const name = this.controlName();
    if (!name) {
      return null;
    }
    const group = this.parentForm?.control;
    if (!group) {
      return null;
    }
    const control = group.get(name);
    return control instanceof FormControl ? control : null;
  }

  /** Whether embedded mode should show the built-in required error slot. */
  protected showEmbeddedRequiredError(control: FormControl): boolean {
    return (
      !!this.requiredErrorMessage() &&
      !!control.errors?.['required'] &&
      ancShowFieldError(control, this.validateSubmitted())
    );
  }

  private watchControl(control: FormControl | null): void {
    control?.valueChanges
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        Promise.resolve().then(() => this.updateHasValue());
        this.cdr.markForCheck();
      });
    control?.statusChanges
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cdr.markForCheck());
  }

  /** One-shot: parent patchValue may run before ngx binds — never hook valueChanges (CD loop). */
  private syncEmbeddedValueOnce(control: FormControl | null): void {
    if (this.embeddedValueSynced || !isNgxPickerWriteValue(control?.value)) {
      return;
    }
    const directive = this.pickerDirective ?? this.projectedPicker;
    if (!directive) {
      return;
    }
    this.embeddedValueSynced = true;
    directive.writeValue(control.value);
    this.updateHasValue();
    this.cdr.markForCheck();
  }

  private activateBodyTheme(): void {
    if (this.themeActive) {
      return;
    }
    this.themeActive = true;
    if (bodyThemeRefCount++ === 0) {
      document.body.classList.add(BODY_THEME_CLASS);
    }
  }

  private deactivateBodyTheme(): void {
    if (!this.themeActive) {
      return;
    }
    this.themeActive = false;
    if (--bodyThemeRefCount === 0) {
      document.body.classList.remove(BODY_THEME_CLASS);
    }
  }

  /** Patches ngx setPosition so popup placement uses trigger border box. */
  private patchActivePickerPosition(): void {
    const directive = this.pickerDirective ?? this.projectedPicker;
    if (directive) {
      this.patchPickerPosition(directive);
    }
  }

  private patchPickerPosition(directive: DaterangepickerDirective): void {
    if (this.patchedPickers.has(directive)) {
      return;
    }
    this.patchedPickers.add(directive);

    const originalOutsideClick = directive.outsideClick.bind(directive);
    directive.outsideClick = (event: Event) => {
      const target = event.target as Node | null;
      const popup = directive.picker?.pickerContainer?.nativeElement as
        | HTMLElement
        | undefined;
      if (
        this.internalPickerInteraction ||
        this.isPickerInteractionTarget(target, popup)
      ) {
        return;
      }
      this.resetPickerView();
      originalOutsideClick(event);
    };

    directive.setPosition = () => {
      this.applyPopupPosition(directive);
      if (directive.picker?.isShown) {
        this.attachRepositionListeners(directive);
      }
    };
  }

  private repositionOpenPopup(): void {
    const directive = this.pickerDirective ?? this.projectedPicker;
    if (directive?.picker?.isShown) {
      this.applyPopupPosition(directive);
    }
  }

  /** Re-positions after ngx open/layout so right-edge alignment is stable. */
  private schedulePopupReposition(directive: DaterangepickerDirective): void {
    const reposition = (): void => {
      if (!directive.picker?.isShown) {
        return;
      }
      this.applyPopupPosition(directive);
      this.attachRepositionListeners(directive);
    };

    requestAnimationFrame(reposition);
    setTimeout(reposition, 0);
    setTimeout(reposition, 50);
  }

  /**
   * Positions `.md-drppicker` flush with the trigger border:
   * left align → popup left = field left; right align → popup right = field right.
   */
  private applyPopupPosition(directive: DaterangepickerDirective): void {
    const popup = directive.picker?.pickerContainer?.nativeElement as
      | HTMLElement
      | undefined;
    if (!popup) {
      setTimeout(() => this.applyPopupPosition(directive), 0);
      return;
    }

    this.decoratePopup(popup);

    if (this.variant === 'filter' && this.filterDisplay === 'button') {
      this.clearPopupInlinePosition(popup);
      return;
    }

    // ngx-daterangepicker-material ships `.md-drppicker { transition: .1s ease-in-out; }`
    // (a catch-all `transition: all`) — every `left`/`top` change we apply below
    // then animates instead of snapping, which is what read as the popup
    // "sliding" into place from the wrong corner instead of appearing already
    // positioned, as it should for a click-to-open menu.
    popup.style.setProperty('transition', 'none', 'important');

    this.portalPopupToBody(popup);
    this.observePopupResize(popup, directive);

    if (this.popupPendingReveal) {
      popup.style.setProperty('visibility', 'hidden', 'important');
    }

    // Several independent triggers can each call `applyPopupPosition` around the
    // same time (this method's own callers, ngx's own `setPosition()`, and the
    // ResizeObserver above reacting to `linkedCalendars`' second month finishing
    // its layout) — reading `getBoundingClientRect()` synchronously in each means
    // whichever happens to run LAST wins, even if it catches the popup mid-reflow
    // (e.g. a transient narrower width) rather than its settled size. Coalescing
    // into one rAF-deferred read+write per burst — cancelling any still-pending
    // one — guarantees the applied position reflects the state after that burst
    // of calls has actually finished, not whichever one raced to the front.
    if (this.pendingPositionFrame !== undefined) {
      cancelAnimationFrame(this.pendingPositionFrame);
    }
    this.pendingPositionFrame = requestAnimationFrame(() => {
      this.pendingPositionFrame = undefined;
      this.computePopupPosition(popup, directive, 0);
    });
  }

  /**
   * `linkedCalendars`'s second month can take more than one animation frame to
   * finish rendering (its own async change-detection pass, not just layout), so
   * a single rAF defer can still catch the popup mid-reflow (e.g. one month's
   * width instead of the final two-month width). After applying, re-measure one
   * more frame later; if the width is still changing, recompute again — capped
   * so a genuinely unstable popup can't loop forever.
   */
  private computePopupPosition(
    popup: HTMLElement,
    directive: DaterangepickerDirective,
    settleAttempt: number
  ): void {
    if (!directive.picker?.isShown) {
      return;
    }

    const widthBefore = popup.getBoundingClientRect().width;
    const trigger = this.getPopupTriggerElement();
    const triggerRect = trigger.getBoundingClientRect();
    const popupHeight =
      popup.getBoundingClientRect().height || popup.offsetHeight || 320;
    const viewportBottom = window.innerHeight - STICKY_FOOTER_CLEARANCE_PX;
    const spaceBelow = viewportBottom - triggerRect.bottom - POPUP_GAP_PX;
    const spaceAbove = triggerRect.top - POPUP_GAP_PX;
    const openUpward = popupHeight > spaceBelow && spaceAbove >= spaceBelow;

    let top: number;
    if (openUpward) {
      top = Math.max(POPUP_GAP_PX, triggerRect.top - popupHeight - POPUP_GAP_PX);
      popup.style.setProperty(
        'transform-origin',
        this.resolvedPopupAlign === 'right' ? 'bottom right' : 'bottom left',
        'important'
      );
    } else {
      top = triggerRect.bottom + POPUP_GAP_PX;
      const maxTop = Math.max(
        POPUP_GAP_PX,
        viewportBottom - popupHeight - POPUP_GAP_PX
      );
      top = Math.min(top, maxTop);
      popup.style.setProperty(
        'transform-origin',
        this.resolvedPopupAlign === 'right' ? 'top right' : 'top left',
        'important'
      );
    }

    popup.style.setProperty('position', 'fixed', 'important');
    popup.style.setProperty('z-index', `${POPUP_Z_INDEX}`, 'important');
    popup.style.setProperty('top', `${Math.round(top)}px`, 'important');
    popup.style.setProperty('bottom', 'auto', 'important');
    popup.style.setProperty('margin-top', '0', 'important');

    const popupWidth = popup.getBoundingClientRect().width || popup.offsetWidth;
    if (!popupWidth) {
      // Width unknown on the first pass — anchor by edge; later passes clamp.
      popup.style.setProperty(
        this.resolvedPopupAlign === 'left' ? 'left' : 'right',
        `${Math.round(
          this.resolvedPopupAlign === 'left'
            ? triggerRect.left
            : window.innerWidth - triggerRect.right
        )}px`,
        'important'
      );
      popup.style.setProperty(
        this.resolvedPopupAlign === 'left' ? 'right' : 'left',
        'auto',
        'important'
      );
      this.scheduleSettleCheck(popup, directive, widthBefore, settleAttempt);
      return;
    }

    // Always anchor by `left` and clamp inside the viewport so RTL (mirrored align)
    // and narrow viewports cannot push the calendar off-screen.
    const desiredLeft =
      this.resolvedPopupAlign === 'left'
        ? triggerRect.left
        : triggerRect.right - popupWidth;
    const maxLeft = Math.max(
      POPUP_GAP_PX,
      window.innerWidth - popupWidth - POPUP_GAP_PX
    );
    const left = Math.min(Math.max(desiredLeft, POPUP_GAP_PX), maxLeft);
    popup.style.setProperty('left', `${Math.round(left)}px`, 'important');
    popup.style.setProperty('right', 'auto', 'important');
    this.scheduleSettleCheck(popup, directive, widthBefore, settleAttempt);
  }

  /**
   * One more frame later, re-measure: if the popup's width is still changing
   * (ngx still mid-render), recompute against the newer width. Stops once two
   * consecutive frames agree, or after a handful of attempts so a genuinely
   * unstable popup (e.g. a calendar that never settles) can't loop forever.
   */
  private scheduleSettleCheck(
    popup: HTMLElement,
    directive: DaterangepickerDirective,
    widthBefore: number,
    settleAttempt: number
  ): void {
    if (settleAttempt >= 10) {
      this.revealSettledPopup(popup);
      return;
    }
    requestAnimationFrame(() => {
      if (!directive.picker?.isShown) {
        return;
      }
      const widthNow = popup.getBoundingClientRect().width;
      if (Math.round(widthNow) !== Math.round(widthBefore)) {
        this.computePopupPosition(popup, directive, settleAttempt + 1);
      } else {
        this.revealSettledPopup(popup);
      }
    });
  }

  /** Un-hides a popup that was held invisible (see `open()`) once its position has settled. */
  private revealSettledPopup(popup: HTMLElement): void {
    if (!this.popupPendingReveal) {
      return;
    }
    this.popupPendingReveal = false;
    popup.style.removeProperty('visibility');
  }

  /** Lets SCSS anchor the filter-button popup to the visible trigger. */
  private clearPopupInlinePosition(popup: HTMLElement): void {
    popup.style.removeProperty('position');
    popup.style.removeProperty('z-index');
    popup.style.removeProperty('top');
    popup.style.removeProperty('left');
    popup.style.removeProperty('right');
    popup.style.removeProperty('bottom');
    popup.style.removeProperty('margin-top');
    popup.style.removeProperty('transform-origin');
  }

  /**
   * ngx renders `.md-drppicker` inside scrollable side panels (`overflow: hidden`,
   * `transform` on ng-scrollbar). Moving it to `body` keeps `position: fixed` aligned
   * to the viewport and above the side panel (z-index 1010).
   */
  private portalPopupToBody(popup: HTMLElement): void {
    if (popup.parentElement !== document.body) {
      document.body.appendChild(popup);
    }
  }

  /**
   * Hides the ngx popup and removes portaled `.md-drppicker` nodes from `document.body`.
   * Required because {@link portalPopupToBody} detaches the popup from the component tree;
   * without cleanup, destroyed side-panel forms leave visible calendars on the page.
   */
  private dismissAndCleanupPicker(): void {
    this.disconnectPopupResizeObserver();
    const directive = this.pickerDirective ?? this.projectedPicker;
    if (directive) {
      try {
        directive.hide();
      } catch {
        // ngx may already be torn down during route transitions.
      }

      const popup = directive.picker?.pickerContainer?.nativeElement as
        | HTMLElement
        | undefined;
      if (popup) {
        this.removePortaledPopup(popup);
      }
    }

    for (const popup of [...this.decoratedPopups]) {
      this.removePortaledPopup(popup);
    }
    this.decoratedPopups = [];
    this.resetPickerView();
  }

  /** Strip open-state chrome and drop orphaned popups portaled to `body`. */
  private removePortaledPopup(popup: HTMLElement): void {
    popup.classList.remove('shown');
    popup.style.setProperty('display', 'none', 'important');
    this.clearPopupInlinePosition(popup);
    if (popup.parentElement === document.body) {
      popup.remove();
    }
  }

  /** Visible input/button whose border box anchors the popup. */
  private getPopupTriggerElement(): HTMLElement {
    const root = this.host.nativeElement;

    if (this.variant === 'filter' && this.filterDisplay === 'button') {
      const filterButton = root.querySelector(
        '.anc-date-picker__filter-trigger button[type="button"]'
      ) as HTMLElement | null;
      if (filterButton) {
        return filterButton;
      }
    }

    const filterInput = root.querySelector(
      '.anc-date-picker__input:not(.anc-date-picker__input--visually-hidden)'
    ) as HTMLElement | null;
    if (filterInput) {
      return filterInput;
    }

    const filterButton = root.querySelector(
      '.anc-date-picker__filter--button button[type="button"]'
    ) as HTMLElement | null;
    if (filterButton) {
      return filterButton;
    }

    const fieldInput = root.querySelector(
      '.anc-date-picker__control input[ngxDaterangepickerMd]'
    ) as HTMLElement | null;
    if (fieldInput) {
      return fieldInput;
    }

    return (root.querySelector('input[ngxDaterangepickerMd]') as HTMLElement) ?? root;
  }

  // ---- DS month/year picker overlay (replaces ngx native month/year selects) ----

  /** Live ngx picker component instance, or undefined before the popup mounts. */
  private get pickerInstance() {
    return (this.pickerDirective ?? this.projectedPicker)?.picker;
  }

  /** Resolve the real ngx SideEnum value off the live instance (enum isn't exported). */
  private sideEnumValue(side: AncDatePickerSide) {
    const e = this.pickerInstance?.sideEnum;
    if (!e) {
      return undefined;
    }
    return side === 'right' ? e.right : e.left;
  }

  private displayedYear(side: AncDatePickerSide): number {
    const cal =
      side === 'right'
        ? this.pickerInstance?.rightCalendar
        : this.pickerInstance?.leftCalendar;
    return cal?.month?.year?.() ?? new Date().getFullYear();
  }

  protected openMonthView(side: AncDatePickerSide): void {
    this.activeSide.set(side);
    this.viewYear.set(this.displayedYear(side));
    this.pickerView.set('month');
    this.refreshOverlay();
  }

  protected onTitleClick(): void {
    this.pickerView.set(this.pickerView() === 'year' ? 'month' : 'year');
    this.refreshOverlay();
  }

  protected onOverlayPrev(): void {
    const step = this.pickerView() === 'year' ? YEAR_GRID_SIZE : 1;
    this.viewYear.update((y) => y - step);
    this.pickerRev.update((n) => n + 1);
  }

  protected onOverlayNext(): void {
    const step = this.pickerView() === 'year' ? YEAR_GRID_SIZE : 1;
    this.viewYear.update((y) => y + step);
    this.pickerRev.update((n) => n + 1);
  }

  protected selectMonth(monthIndex: number): void {
    const side = this.sideEnumValue(this.activeSide());
    if (side === undefined) {
      return;
    }
    this.internalPickerInteraction = true;
    this.pickerInstance?.monthOrYearChanged(monthIndex, this.viewYear(), side);
    // ngx mutates its calendar but only re-renders itself on its own events;
    // force its local CD so the date grid reflects the new month/year.
    this.flushPicker();
    this.closeOverlayView();
    this.clearInternalPickerInteractionSoon();
  }

  /** Force the ngx picker to re-render after an external navigation call. */
  private flushPicker(): void {
    const ref = (this.pickerInstance as unknown as {
      ref?: ChangeDetectorRef;
    })?.ref;
    ref?.detectChanges();
  }

  protected selectYear(year: number): void {
    this.viewYear.set(year);
    this.pickerView.set('month');
    this.pickerRev.update((n) => n + 1);
  }

  protected closeOverlayView(): void {
    this.pickerView.set('date');
    this.cdr.markForCheck();
  }

  protected overlayTitle(): string {
    return String(this.viewYear());
  }

  protected yearList(): number[] {
    const start = this.viewYear() - YEAR_GRID_OFFSET;
    return Array.from({ length: YEAR_GRID_SIZE }, (_v, i) => start + i);
  }

  protected isMonthSelected(monthIndex: number): boolean {
    this.pickerRev();
    const sel = this.pickerInstance?.startDate;
    return (
      !!sel && sel.year() === this.viewYear() && sel.month() === monthIndex
    );
  }

  protected isYearSelected(year: number): boolean {
    this.pickerRev();
    const sel = this.pickerInstance?.startDate;
    return !!sel && sel.year() === year;
  }

  protected isMonthDisabled(monthIndex: number): boolean {
    const y = this.viewYear();
    const min = this.pickerInstance?.minDate;
    const max = this.pickerInstance?.maxDate;
    if (min && (y < min.year() || (y === min.year() && monthIndex < min.month()))) {
      return true;
    }
    if (max && (y > max.year() || (y === max.year() && monthIndex > max.month()))) {
      return true;
    }
    return false;
  }

  protected isYearDisabled(year: number): boolean {
    const min = this.pickerInstance?.minDate;
    const max = this.pickerInstance?.maxDate;
    return (!!min && year < min.year()) || (!!max && year > max.year());
  }

  private normalizeBoundDate(
    value: AncDatePickerBound | undefined
  ): Dayjs | undefined {
    if (value == null) {
      return undefined;
    }
    if (dayjs.isDayjs(value)) {
      return value.isValid() ? value : undefined;
    }
    if (
      typeof value === 'object' &&
      'toDate' in value &&
      typeof value.toDate === 'function'
    ) {
      const parsed = dayjs(value.toDate());
      return parsed.isValid() ? parsed : undefined;
    }
    // No locale.format — ISO (`YYYY-MM-DD`) and Date must not go through MM/DD/YYYY.
    const parsed = dayjs(value as string | Date | number);
    return parsed.isValid() ? parsed : undefined;
  }

  /** Coerce parent range (ISO strings / Moment / Date) to Dayjs for ngx writeValue. */
  private normalizeRangeValue(
    range: AncDatePickerValue | null
  ): AncDatePickerValue | null {
    if (range == null) {
      return null;
    }
    const startDate =
      range.startDate != null
        ? this.normalizeBoundDate(range.startDate as AncDatePickerBound)
        : undefined;
    const endDate =
      range.endDate != null
        ? this.normalizeBoundDate(range.endDate as AncDatePickerBound)
        : undefined;
    if (startDate == null || endDate == null) {
      return null;
    }
    return { ...range, startDate, endDate };
  }

  private resetPickerView(): void {
    if (this.pickerView() !== 'date') {
      this.pickerView.set('date');
      this.cdr.markForCheck();
    }
  }

  private refreshOverlay(): void {
    this.pickerRev.update((n) => n + 1);
    const popup = this.pickerInstance?.pickerContainer
      ?.nativeElement as HTMLElement | undefined;
    if (popup) {
      this.repositionOverlay(popup);
    }
    this.cdr.markForCheck();
  }

  private repositionOverlay(popup: HTMLElement): void {
    const r = popup.getBoundingClientRect();
    this.overlayStyle.set({
      position: 'fixed',
      top: `${Math.round(r.top)}px`,
      left: `${Math.round(r.left)}px`,
      width: `${Math.round(r.width)}px`,
      height: `${Math.round(r.height)}px`,
    });
  }

  /** Hide the native selects, make the month header a trigger, track the popup. */
  private decoratePopup(popup: HTMLElement): void {
    if (!this.decoratedPopups.includes(popup)) {
      this.decoratedPopups.push(popup);
      popup.addEventListener('pointerdown', this.onPopupHeaderPointerDown, true);
      popup.addEventListener('mousedown', this.onPopupHeaderPointerDown, true);
      popup.addEventListener('click', this.onPopupHeaderClick, true);
    }
    if (this.pickerView() !== 'date') {
      this.repositionOverlay(popup);
    }
  }

  protected onOverlayInternalEvent(event: Event): void {
    this.stopPickerInternalEvent(event);
  }

  private readonly onPopupHeaderPointerDown = (event: Event): void => {
    if (!this.isMonthHeaderEvent(event)) {
      return;
    }
    this.stopPickerInternalEvent(event);
  };

  private readonly onPopupHeaderClick = (event: Event): void => {
    if (!this.isMonthHeaderEvent(event)) {
      return;
    }
    this.stopPickerInternalEvent(event);
    const target = event.target as HTMLElement | null;
    // In single mode ngx tags its one visible calendar `.right` via ngClass,
    // yet its data/handlers run on the LEFT side — so only trust `.right`
    // when two calendars are actually rendered.
    const calendar = target?.closest('.drp-calendar, .calendar');
    const side: AncDatePickerSide =
      !this.singleDatePicker && calendar?.classList.contains('right')
        ? 'right'
        : 'left';
    this.openMonthView(side);
  };

  private detachHeaderTriggers(): void {
    this.decoratedPopups.forEach((popup) => {
      popup.removeEventListener('pointerdown', this.onPopupHeaderPointerDown, true);
      popup.removeEventListener('mousedown', this.onPopupHeaderPointerDown, true);
      popup.removeEventListener('click', this.onPopupHeaderClick, true);
    });
    this.decoratedPopups = [];
  }

  private isMonthHeaderEvent(event: Event): boolean {
    const target = event.target as HTMLElement | null;
    return !!target?.closest('th.month');
  }

  private stopPickerInternalEvent(event: Event): void {
    this.internalPickerInteraction = true;
    event.preventDefault();
    event.stopPropagation();
    (event as Event & { stopImmediatePropagation?: () => void })
      .stopImmediatePropagation?.();
    this.clearInternalPickerInteractionSoon();
  }

  private clearInternalPickerInteractionSoon(): void {
    setTimeout(() => {
      this.internalPickerInteraction = false;
    }, 0);
  }

  private isPickerInteractionTarget(
    target: Node | null,
    popup?: HTMLElement
  ): boolean {
    if (!target) {
      return false;
    }
    if (popup?.contains(target)) {
      return true;
    }
    // A click inside ANOTHER open picker is still picker chrome, not an outside
    // click. Popups are portaled to <body>, and ngx's own check only compares
    // against its input element while its Apply/Cancel/day buttons carry no
    // `ngx-daterangepicker-action` class — so cancelling one calendar hid every
    // other open one (three Contract Details dates closed together).
    const element =
      target instanceof Element ? target : target.parentElement;
    if (element?.closest('.md-drppicker')) {
      return true;
    }
    // Overlay (month/year grid) and trigger live in the host — not the popup.
    return this.host.nativeElement.contains(target);
  }

  private attachRepositionListeners(directive: DaterangepickerDirective): void {
    this.detachRepositionListeners();
    this.repositionListener = () => {
      if (directive.picker?.isShown) {
        this.applyPopupPosition(directive);
      } else {
        this.detachRepositionListeners();
      }
    };
    window.addEventListener('scroll', this.repositionListener, true);
    window.addEventListener('resize', this.repositionListener);
  }

  private detachRepositionListeners(): void {
    if (!this.repositionListener) {
      return;
    }
    window.removeEventListener('scroll', this.repositionListener, true);
    window.removeEventListener('resize', this.repositionListener);
    this.repositionListener = undefined;
  }

  /**
   * Right-aligned popups (RTL, or explicit `popupAlign="right"`) anchor via a
   * `left` computed as `triggerRect.right - popupWidth` — unlike the left-aligned
   * anchor (`triggerRect.left`, width-independent), this is only as correct as
   * the `popupWidth` measured at the time. `linkedCalendars` renders its second
   * month slightly after the first, so the width measured by the initial
   * `schedulePopupReposition` passes can undershoot the final double-width
   * panel, locking in a `left` that then overflows past the viewport's right
   * edge once the second month appends. A `ResizeObserver` on the popup itself
   * re-applies the position on every subsequent content-size change, closing
   * that gap for good instead of hoping a fixed timeout is long enough.
   */
  private observePopupResize(popup: HTMLElement, directive: DaterangepickerDirective): void {
    if (this.observedPopup === popup) {
      return;
    }
    this.popupResizeObserver?.disconnect();
    this.observedPopup = popup;
    this.popupResizeObserver = new ResizeObserver(() => {
      if (directive.picker?.isShown) {
        this.applyPopupPosition(directive);
      } else {
        this.disconnectPopupResizeObserver();
      }
    });
    this.popupResizeObserver.observe(popup);
  }

  private disconnectPopupResizeObserver(): void {
    this.popupResizeObserver?.disconnect();
    this.popupResizeObserver = undefined;
    this.observedPopup = undefined;
    this.popupPendingReveal = false;
    if (this.pendingPositionFrame !== undefined) {
      cancelAnimationFrame(this.pendingPositionFrame);
      this.pendingPositionFrame = undefined;
    }
  }
}
